import React, { useMemo } from 'react';
import { View, Text, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { formatCurrency } from '@/utils/common/formatting';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { GlassCard } from '../GlassCard';
import { SectionHeader } from '../SectionHeader';

export interface RecurringChargeItem {
  name: string;
  amount: number | null;
  frequency: string;
}

interface RecurringChargesCardProps {
  charges: RecurringChargeItem[];
  style?: StyleProp<ViewStyle>;
}

export const RecurringChargesCard: React.FC<RecurringChargesCardProps> = React.memo(({
  charges,
  style,
}) => {
  const { colors } = useThemeColors();

  const total = useMemo(
    () => charges.reduce((sum, c) => sum + (c.amount ?? 0), 0),
    [charges],
  );

  if (charges.length === 0) return null;

  return (
    <View style={style}>
      <SectionHeader title="Recurring Charges" />
      <GlassCard padding={0} borderRadius={16}>
        {charges.map((charge, index) => (
          <View
            key={`${charge.name}-${index}`}
            style={[styles.chargeRow, index < charges.length - 1 && [styles.chargeRowBorder, { borderBottomColor: colors.separator }]]}
          >
            <View style={styles.chargeInfo}>
              <Text style={[styles.chargeName, { color: colors.text.tertiary }]}>{charge.name}</Text>
              <Text style={[styles.chargeFrequency, { color: colors.text.dim }]}>{charge.frequency}</Text>
            </View>
            <Text style={[styles.chargeAmount, { color: colors.text.tertiary }]}>
              {charge.amount != null ? formatCurrency(charge.amount) : '—'}
            </Text>
          </View>
        ))}

        <View style={[styles.totalRow, { borderTopColor: colors.card.border }]}>
          <Text style={[styles.totalLabel, { color: colors.text.subtle }]}>Total Monthly</Text>
          <Text style={[styles.totalAmount, { color: colors.text.primary }]}>{formatCurrency(total)}</Text>
        </View>
      </GlassCard>
    </View>
  );
});

RecurringChargesCard.displayName = 'RecurringChargesCard';

const styles = StyleSheet.create({
  chargeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 14,
  },
  chargeRowBorder: {
    borderBottomWidth: 1,
  },
  chargeInfo: {
    gap: 1,
  },
  chargeName: {
    fontSize: 14,
    fontWeight: '500',
  },
  chargeFrequency: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  chargeAmount: {
    fontSize: 14,
    fontWeight: '600',
  },
  totalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderTopWidth: 1,
  },
  totalLabel: {
    ...REDESIGN_TYPOGRAPHY.balanceLabel,
    fontSize: 13,
  },
  totalAmount: {
    fontSize: 16,
    fontWeight: '700',
  },
});
